import { Link } from '@tanstack/react-router';
import {
  type ColumnFiltersState,
  createColumnHelper,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import { format, subDays } from 'date-fns';
import { CalendarDays, DollarSign, Users } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';

import {
  type BulkChargeHistoryItem,
  useGetBulkChargeHistory,
} from '~entities/payment/api/bulk-charge-history';

import {
  EnrollioTable,
  EnrollioTableColumnToggle,
} from '~shared/components/enrollio-table';
import { DataTableColumnHeader } from '~shared/components/table';
import { useStudioDateFormatter } from '~shared/hooks/useStudioDateFormatter';
import { Button } from '~shared/shadcn/ui/button';

import { DateRangePicker } from './date-range-picker';

const columnHelper = createColumnHelper<BulkChargeHistoryItem>();

/**
 * Stable fallback so `data ?? []` does not allocate a new array each render.
 * A fresh `[]` on every render makes EnrollioTable/autoResetPageIndex loop and
 * freezes the tab when the date range query key flips (ENR-3465).
 */
const EMPTY_BULK_CHARGE_HISTORY: Array<BulkChargeHistoryItem> = [];

/** BE already returns human-readable dates like "Sep 17, 2026 3:45pm". */
const isIsoTimestamp = (value: string) =>
  /^\d{4}-\d{2}-\d{2}/.test(value) || value.includes('T');

const formatChargeHistoryDate = (
  rawDate: string,
  formatInStudioTz: (date: string | Date, fmt: string) => string,
): string => {
  if (!rawDate) return '';
  if (!isIsoTimestamp(rawDate)) {
    return rawDate;
  }
  try {
    return formatInStudioTz(rawDate, 'MMM dd, yyyy, hh:mm a');
  } catch {
    return rawDate;
  }
};

export const ClassChargeHistoryTable = () => {
  const { formatInStudioTz } = useStudioDateFormatter();
  const [date, setDate] = useState<DateRange | undefined>({
    from: subDays(new Date(), 6),
    to: new Date(),
  });

  const startDate = date?.from ? format(date.from, 'yyyy-MM-dd') : '';
  const endDate = date?.to ? format(date.to, 'yyyy-MM-dd') : '';

  const { data, isLoading, isFetching } = useGetBulkChargeHistory({
    startDate,
    endDate,
  });

  const cols = useMemo(
    () => [
      columnHelper.accessor('chargeName', {
        id: 'chargeName',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Charge Name'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='px-4 font-medium'>
            {info.getValue()?.trim() || 'N/A'}
          </div>
        ),
      }),
      columnHelper.accessor('bulkPaymentId', {
        id: 'id',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Id'
          />
        ),
        cell: (info) => {
          const id = info.getValue() || '';
          const shortId = id.length > 10 ? `…${id.slice(-7)}` : id;
          return (
            <div className='px-4 font-mono text-xs text-muted-foreground' title={id}>
              {shortId}
            </div>
          );
        },
      }),
      columnHelper.accessor('date', {
        id: 'date',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Date'
            disableHiding
          />
        ),
        cell: (info) => {
          const formatted = formatChargeHistoryDate(
            info.getValue(),
            formatInStudioTz,
          );
          return (
            <div className='px-4'>
              <Button
                variant='link'
                asChild
                className='h-auto p-0 text-custom-secondary-1'
              >
                <Link
                  to={'/payment/student-charge-history'}
                  className='flex items-center gap-2'
                  search={{
                    id: info.row.original.bulkPaymentId,
                  }}
                >
                  <CalendarDays className='size-4 shrink-0 text-muted-foreground' />
                  <span>{formatted || 'View details'}</span>
                </Link>
              </Button>
            </div>
          );
        },
      }),
      columnHelper.accessor('totalAmount', {
        id: 'totalAmount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Total Amount'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='flex items-center gap-2 px-4'>
            <DollarSign className='size-4 text-green-500' />{' '}
            {Number(info.getValue()).toFixed(2)}
          </div>
        ),
      }),
      columnHelper.accessor('studentsCount', {
        id: 'studentsCount',
        header: (info) => (
          <DataTableColumnHeader
            className='pl-4 text-black'
            column={info.column}
            title='Students Count'
            disableHiding
          />
        ),
        cell: (info) => (
          <div className='flex items-center gap-2 px-4'>
            <Users className='size-4 text-blue-500' />
            {info.getValue()}
          </div>
        ),
      }),
    ],
    [formatInStudioTz],
  );

  const [pagination, setPagination] = useState<{
    pageIndex: number;
    pageSize: number;
  }>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // Raw Mongo ids are noisy in the default layout; still available via Columns
    id: false,
  });

  return (
    <EnrollioTable
      ariaLabel='Class charge history'
      totalLabel='Charges'
      filterHeader={<DateRangePicker date={date} setDate={setDate} />}
      toolbar={(table) => <EnrollioTableColumnToggle table={table} />}
      columns={cols}
      data={data ?? EMPTY_BULK_CHARGE_HISTORY}
      // Avoid full-table overlay while the date popover is open (isFetching
      // on every range click was freezing the page for Lester ENR-3423 QA).
      isUpdating={isLoading || (isFetching && !data)}
      sorting={sorting}
      onSortingChange={setSorting}
      columnFilters={columnFilters}
      onColumnFiltersChange={setColumnFilters}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={setColumnVisibility}
      pagination={pagination}
      onPaginationChange={setPagination}
    />
  );
};
